import React, { useId } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Chip, Link, Typography } from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';
import { AwaitingClient, OverdueClient, StaffSuccessUpdates } from '../types';

export interface ClientUpdatesListProps {
  /** One coach's rollup; `null` / `undefined` (no active clients) reads as everyone being up to date. */
  updates: StaffSuccessUpdates | null | undefined;
}

const formatDate = (iso: string): string => {
  const date = parseISO(iso);
  return isValid(date) ? format(date, 'd MMM yyyy') : 'an unknown date';
};

/** Amber that deepens with age: under 14 days, 14–41 days, then 42 days or more. */
const ageChipColors = (daysOverdue: number): { bg: string; text: string } => {
  if (daysOverdue >= 42) return { bg: '#9A3412', text: '#FFFFFF' };
  if (daysOverdue >= 14) return { bg: '#FDE68A', text: '#78350F' };
  return { bg: '#FEF3C7', text: '#92400E' };
};

const clientPath = (clientId: string) => `/clients/${encodeURIComponent(clientId)}`;

const ClientLink: React.FC<{ clientId: string; name: string }> = ({ clientId, name }) => (
  <Link component={RouterLink} to={clientPath(clientId)} underline="hover" variant="body2" sx={{ fontWeight: 600 }}>
    {name}
  </Link>
);

const Row: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="li"
    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1.25, '& + &': { borderTop: 1, borderColor: 'divider' } }}
  >
    {children}
  </Box>
);

const OverdueRow: React.FC<{ client: OverdueClient }> = ({ client }) => {
  const colors = ageChipColors(client.days_overdue);
  const days = client.days_overdue === 1 ? 'day' : 'days';
  return (
    <Row>
      <Box sx={{ minWidth: 0 }}>
        <ClientLink clientId={client.client_id} name={client.client_name} />
        <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
          Last update {formatDate(client.last_update_at)}
        </Typography>
      </Box>
      <Chip
        size="small"
        label={`${client.days_overdue} ${days} overdue`}
        sx={{ flexShrink: 0, fontWeight: 600, bgcolor: colors.bg, color: colors.text }}
      />
    </Row>
  );
};

const weeksAgo = (days: number): string => {
  const weeks = Math.floor(days / 7);
  if (weeks < 1) return 'less than a week ago';
  return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
};

const AwaitingRow: React.FC<{ client: AwaitingClient }> = ({ client }) => (
  <Row>
    <Box sx={{ minWidth: 0 }}>
      <ClientLink clientId={client.client_id} name={client.client_name} />
      <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
        First session {formatDate(client.first_session_at)} · {weeksAgo(client.days_since_first_session)}
      </Typography>
    </Box>
  </Row>
);

// An explicit role keeps the list semantics that Safari drops when list-style is none.
const LIST_SX = { listStyle: 'none', m: 0, p: 0 } as const;

/**
 * One coach's overdue clients (most overdue first), then those still awaiting a first update, each linked to Client
 * Details. Shared by the team drawer and the coach's own page.
 */
export const ClientUpdatesList: React.FC<ClientUpdatesListProps> = ({ updates }) => {
  const awaitingId = useId();
  const overdueClients = updates?.overdue_clients ?? [];
  const awaitingClients = updates?.awaiting_clients ?? [];

  return (
    <>
      {overdueClients.length > 0 ? (
        <Box component="ul" role="list" aria-label="Overdue clients" sx={LIST_SX}>
          {overdueClients.map((client) => (
            <OverdueRow key={client.client_id} client={client} />
          ))}
        </Box>
      ) : null}
      {awaitingClients.length > 0 ? (
        <Box component="section" aria-labelledby={awaitingId} sx={{ mt: overdueClients.length > 0 ? 2 : 1 }}>
          <Typography id={awaitingId} component="h3" variant="subtitle2" sx={{ color: 'text.secondary', pb: 0.5 }}>
            Awaiting a first update
          </Typography>
          <Box component="ul" role="list" sx={LIST_SX}>
            {awaitingClients.map((client) => (
              <AwaitingRow key={client.client_id} client={client} />
            ))}
          </Box>
        </Box>
      ) : null}
      {overdueClients.length === 0 && awaitingClients.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
          Every active client is up to date.
        </Typography>
      ) : null}
    </>
  );
};
